import { useMemo, useEffect, useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import { useSelector } from '@xstate/react'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'
import SharedCartComments from '../Comments/SharedCartComments'
import { isLEAdmin } from 'selectors/accountSelectors'
import { fetchCruiseOffer } from 'actions/CruiseActions'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { getIsEnabledLuxPlusDiscountedCruise } from 'luxPlus/selectors/featureToggle'
import { toAutoDeleteCandidates, toCurrentStates } from '../multiCartUtils'

interface Props {
  multiCartItem: App.MultiCartCruiseItem
  className?: string
  hideTitleAndDates?: boolean
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  autoDeleteIfUnavailable?: boolean
  tileVariant?: 'default' | 'condensed'
}

function CruiseMultiCartReviewTile({
  multiCartItem,
  className,
  hideTitleAndDates = false,
  hideAvailabilityDetails = false,
  disableOverlay = false,
  autoDeleteIfUnavailable = true,
  tileVariant = 'default',
}: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()

  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const isAdmin = useAppSelector(isLEAdmin)
  const isLuxPlusDiscountedCruiseEnabled = useAppSelector(getIsEnabledLuxPlusDiscountedCruise)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type !== 'default'

  // Get cruise offer from Redux
  const {
    cruiseOffer,
    cruiseOfferLoading,
    cruiseOfferError,
  } = useAppSelector((state) => ({
    cruiseOffer: state.cruise.cruiseOffers[multiCartItem.offerId],
    cruiseOfferLoading: state.cruise.cruiseOffersLoading[multiCartItem.offerId],
    cruiseOfferError: state.cruise.cruiseOffersErrors[multiCartItem.offerId],
  }), shallowEqual)

  // ============================================================================
  // STATE CALCULATIONS
  // ============================================================================
  const isDeleted = multiCartItem.state === 'deleted'
  const saveForLater = multiCartItem.state === 'save_for_later'
  const isPast = dateIsBeforeOrSame(new Date(multiCartItem.startDate), new Date())
  const isBookable = !isPast && !cruiseOfferError && !!cruiseOffer
  const currentStates = useMemo(() => toCurrentStates([multiCartItem]), [multiCartItem])

  useEffect(() => {
    if (multiCartItem.offerId && !cruiseOffer && !cruiseOfferLoading && !cruiseOfferError) {
      dispatch(fetchCruiseOffer(multiCartItem.offerId))
    }
  }, [dispatch, multiCartItem.offerId, cruiseOffer, cruiseOfferLoading, cruiseOfferError])

  // ============================================================================
  // AUTO DELETE UNAVAILABLE ITEM EFFECT
  // ============================================================================
  useEffect(() => {
    // Fetch errors are recoverable, so only explicit past-date states auto-delete.
    const cruiseIsNoLongerSellable = isPast
    const shouldAutoDeleteCruise =
      activeCartId &&
      cruiseOffer &&
      cruiseIsNoLongerSellable &&
      !saveForLater &&
      !cruiseOfferLoading &&
      !isDeleted &&
      autoDeleteIfUnavailable

    if (!shouldAutoDeleteCruise || !activeCartId) {
      return
    }

    multiCartActorRef.send({
      type: 'auto delete unavailable item candidates',
      cartId: activeCartId,
      candidates: toAutoDeleteCandidates([multiCartItem.id]),
    })
  }, [isPast, saveForLater, cruiseOfferLoading, cruiseOffer, isDeleted, autoDeleteIfUnavailable, activeCartId, multiCartActorRef, multiCartItem.id])

  const loading = cruiseOfferLoading
  const error = cruiseOfferError

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id],
      saveForLater: !saveForLater,
    })
  }, [activeCartId, multiCartItem.id, saveForLater, multiCartReviewActorRef])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: [multiCartItem.id],
      currentStates,
    })
  }, [activeCartId, currentStates, multiCartItem.id, multiCartActorRef])

  const formattedDateRange = useMemo(() => {
    const startDate = moment(multiCartItem.startDate).format(DMY_CASUAL_FORMAT)
    const endDate = moment(multiCartItem.endDate).format(DMY_CASUAL_FORMAT)
    return `${startDate} - ${endDate}`
  }, [multiCartItem.startDate, multiCartItem.endDate])

  const packageTitle = useMemo(() => {
    const sep = intl.formatMessage(middleDotSeparatorMessage)
    const parts: Array<string> = []
    const occupancy = multiCartItem.occupancy
    const totalGuests = occupancy.adults + (occupancy.children ?? 0) + (occupancy.infants ?? 0)

    if (totalGuests > 0) {
      parts.push(intl.formatMessage(
        {
          id: '38HgjSVAljAwxc1vmc3G',
          defaultMessage: '{count, plural, one {# guest} other {# guests}}',
          description: 'Guest count in cruise multi-cart review tile package title',
        },
        { count: totalGuests },
      ))
    }

    if (multiCartItem.cabinType) {
      parts.push(multiCartItem.cabinType)
    }

    if (multiCartItem.cabinCode && multiCartItem.cabinNumber) {
      parts.push(`${multiCartItem.cabinCode} ${multiCartItem.cabinNumber}`)
    }

    return parts.join(` ${sep} `)
  }, [multiCartItem.occupancy, multiCartItem.cabinType, multiCartItem.cabinCode, multiCartItem.cabinNumber, intl])

  const cruiseURL = useMemo(() => {
    if (!cruiseOffer) return ''
    return `/cruises/${cruiseOffer.id}`
  }, [cruiseOffer])

  const bannerTypeVariant = getBannerTypeVariant(isPast, false, false, !!error)
  const image = cruiseOffer?.images[0]
  const cruiseFallbackTitle = intl.formatMessage(productLabels.cruise)
  const title = cruiseOffer?.name ?? cruiseFallbackTitle

  if (loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (isDeleted) {
    return <UndoDeletedItemsTile
      title={title}
      onUndoClick={() => {
        multiCartActorRef.send({
          itemIds: [multiCartItem.id],
          type: 'undo delete',
        })
      }}
    />
  }

  const publicPrice = multiCartItem.price ?? 0
  const memberPrice = multiCartItem.memberPrice ?? 0
  const displayMemberPrice = isLuxPlusDiscountedCruiseEnabled ? memberPrice : 0
  const luxPlusOfferConfig = isLuxPlusDiscountedCruiseEnabled ? cruiseOffer?.luxPlus : undefined
  const valueForStack = publicPrice

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        to={cruiseURL}
        banner={<CartReviewBanner
          variant={bannerTypeVariant}
        />}
        onDelete={handleDelete}
        content={<CondensedCartReviewTileContent
          title={title}
          dates={formattedDateRange}
          packageTitle={packageTitle}
        />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        price={<CartReviewPriceStack
          price={publicPrice}
          memberPrice={displayMemberPrice}
          value={valueForStack}
          luxPlusOfferConfig={luxPlusOfferConfig}
        />}
      />
    )
  }

  const commentsFooter = activeCartId && isSharedCart ? (
    <SharedCartComments
      multiCartId={activeCartId}
      itemIds={[multiCartItem.id]}
      isAdmin={isAdmin}
      noBorder
    />
  ) : undefined

  return (
    <CartReviewTile
      to={cruiseURL}
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CartReviewTileContent
        title={title}
        dates={formattedDateRange}
        hideTitleAndDates={hideTitleAndDates}
        packageTitle={packageTitle}
        label={<MultiCartParticipantLabel item={multiCartItem} />}
      />}
      price={<CartReviewPriceStack
        price={publicPrice}
        memberPrice={displayMemberPrice}
        value={valueForStack}
        luxPlusOfferConfig={luxPlusOfferConfig}
      />}
      banner={<CartReviewBanner
        variant={bannerTypeVariant}
        to={cruiseURL}
      />}
      footer={commentsFooter}
      isBookable={isBookable}
      saveForLater={saveForLater}
      actions={<CartReviewTileActions
        isBookable={isBookable}
        saveForLater={saveForLater}
        onSaveForLater={handleSaveForLater}
        onDelete={handleDelete}
      />}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
      disableOverlay={disableOverlay}
    />
  )
}

export default CruiseMultiCartReviewTile
